import { CRYSTAL_TYPE_TEXT_COLORS } from '@sorrem-client/ui/shared/crystal-type-colors';
import { capitalize } from '@sorrem-client/ui/shared/text';
import { CRYSTAL_COALESCENCE_MS, EMPYREAL_CRYSTAL_SIZES, getRecipeSystem, resolveEssenceShortfall } from '@sorrem/shared';
import type { EssencePool, EssenceType, RecipeDefinition } from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem } from '@sorrem-client/types';
import { EssenceOrb } from '@sorrem-client/ui/components/EssenceOrb';
import { Button } from '@sorrem/ui';
import { CrystalIcon } from '@sorrem-client/ui/components/CrystalIcon';
import { buildCoalesceRows, canCoalesceIntoSlot } from '@sorrem-client/ui/shared/crystal-coalesce';
import classNames from 'classnames';
import { useMemo, useState, type CSSProperties } from 'react';
import styles from './CoalesceSection.module.scss';

interface CoalesceSectionProps {
    /** Carried slot the crystal goes into. */
    slotIndex: number;
    /** What occupies that slot now. */
    item: UIInventoryItem | undefined;
    /** Coalescences headed for that slot. */
    pending: readonly UICoalescence[];
    /** The essence coalescing can spend. */
    essencePool: EssencePool;
    /** Essence value the run's commission holds back from coalescing; noted when above zero. */
    heldEssenceValue?: number;
    /** Crystals form at once (on the gathering page) rather than over time. */
    isInstant?: boolean;
    onStart: (recipe: RecipeDefinition) => Promise<string | null>;
    onCancel: (coalescenceId: number) => void;
}

function formatSeconds(ms: number): string {
    return `${Math.round(ms / 1000)}s`;
}


/**
 * The crystals that can coalesce into one carried slot: a row per recipe
 * series, a column per size. Sizes the slot can't take are left blank,
 * unaffordable ones are dimmed, and the hovered or focused crystal is
 * described underneath. Lists what is already coalescing into the slot,
 * each with a Cancel.
 */
export function CoalesceSection({ slotIndex, item, pending, essencePool, heldEssenceValue = 0, isInstant = false, onStart, onCancel }: CoalesceSectionProps): JSX.Element {
    const rows = useMemo(() => buildCoalesceRows(getRecipeSystem().getAllRecipes()), []);
    const [focusedRecipeId, setFocusedRecipeId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fittingRows = rows
        .map((row) => ({
            row,
            cells: EMPYREAL_CRYSTAL_SIZES.map((size) => {
                const recipe = row.recipes[size];
                return recipe !== undefined && canCoalesceIntoSlot(item, pending, recipe) ? recipe : undefined;
            }),
        }))
        .filter(({ cells }) => cells.some((recipe) => recipe !== undefined));
    const focusedRecipe = focusedRecipeId === null ? null : getRecipeSystem().getRecipe(focusedRecipeId);
    const focusedShortfall = focusedRecipe === null ? {} : resolveEssenceShortfall(essencePool, focusedRecipe.ingredients);

    const handleFocusRecipe = (recipeId: string): void => {
        setFocusedRecipeId(recipeId);
        setError(null);
    };

    const handleStart = (recipe: RecipeDefinition): void => {
        if (Object.keys(resolveEssenceShortfall(essencePool, recipe.ingredients)).length > 0) {
            setError(`Not enough essence for ${recipe.name}.`);
            return;
        }
        void onStart(recipe).then(setError);
    };

    return (
        <section className={styles.coalesceSection} aria-label={`Coalesce into slot ${slotIndex + 1}`}>
            <h3 className={styles.heading}>{`Coalesce into slot ${slotIndex + 1}`}</h3>
            {heldEssenceValue > 0 && <p className={styles.message}>{`${heldEssenceValue} essence is held for your commission.`}</p>}

            {pending.length > 0 && (
                <ul className={styles.pending}>
                    {pending.map((coalescence) => (
                        <li key={coalescence.id} className={styles.pendingEntry}>
                            <span>{getRecipeSystem().getRecipe(coalescence.recipeId)?.name ?? 'Crystal'}</span>
                            <Button variant='secondary' className={styles.cancel} onClick={() => onCancel(coalescence.id)}>Cancel</Button>
                        </li>
                    ))}
                </ul>
            )}

            {fittingRows.length === 0
                ? <p className={styles.message}>This slot is full.</p>
                : (
                    <div className={styles.grid} onMouseLeave={() => setFocusedRecipeId(null)}>
                        {fittingRows.map(({ row, cells }) => (
                            <div key={row.key} className={styles.row} role='group' aria-label={row.label}>
                                <span className={styles.rowLabel} style={{ '--row-color': getRowColor(row.crystalType) } as CSSProperties}>
                                    {row.label}
                                </span>
                                {cells.map((recipe, index) => {
                                    if (recipe === undefined) {
                                        return <span key={EMPYREAL_CRYSTAL_SIZES[index]} className={styles.emptyCell} />;
                                    }
                                    const isShort = Object.keys(resolveEssenceShortfall(essencePool, recipe.ingredients)).length > 0;
                                    return (
                                        // Native button: a recipe cell is a crystal tile, not a framed Button.
                                        <button
                                            key={recipe.id}
                                            type='button'
                                            className={classNames(styles.cell, isShort && styles.short)}
                                            aria-label={`${recipe.name}${isShort ? ', not enough essence' : ''}`}
                                            aria-disabled={isShort}
                                            onMouseEnter={() => handleFocusRecipe(recipe.id)}
                                            onFocus={() => handleFocusRecipe(recipe.id)}
                                            onClick={() => handleStart(recipe)}
                                        >
                                            <CrystalIcon crystalType={recipe.crystalType} size={recipe.size} dimmed={isShort} className={styles.cellCrystal} />
                                            <span className={styles.cost}>
                                                {(Object.entries(recipe.ingredients) as Array<[EssenceType, number]>).map(([type, amount]) => (
                                                    <span key={type} className={styles.costEntry} title={capitalize(type)}>
                                                        <EssenceOrb type={type} className={styles.essenceOrb} />
                                                        {amount}
                                                    </span>
                                                ))}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

            <div className={styles.details} aria-live='polite'>
                {error !== null && <p className={styles.error}>{error}</p>}
                {error === null && focusedRecipe === null && <p className={styles.message}>Choose a crystal to coalesce.</p>}
                {error === null && focusedRecipe !== null && (
                    <>
                        <p className={styles.detailName}>
                            {isInstant
                                ? `${focusedRecipe.name}: ready the moment you enter. Unbroken, it's lost when the run ends.`
                                : `${focusedRecipe.name}: ${formatSeconds(CRYSTAL_COALESCENCE_MS[focusedRecipe.size])} to coalesce`}
                        </p>
                        {focusedRecipe.description !== undefined && <p>{focusedRecipe.description}</p>}
                        <p className={styles.detailCost}>
                            {(Object.entries(focusedRecipe.ingredients) as Array<[EssenceType, number]>).map(([type, amount]) => (
                                <span key={type} className={classNames(focusedShortfall[type] !== undefined && styles.missing)}>
                                    {`${amount} ${capitalize(type)}${focusedShortfall[type] !== undefined ? ` (${focusedShortfall[type]} short)` : ''}`}
                                </span>
                            ))}
                        </p>
                    </>
                )}
            </div>
        </section>
    );
}

function getRowColor(crystalType: RecipeDefinition['crystalType']): string {
    return CRYSTAL_TYPE_TEXT_COLORS[crystalType];
}

